// Step-up (Parte 14.2 / REQ-AUTH-014): o interceptor detecta `step_up_required`, abre o StepUpDialog, reautentica no
// provedor (`prompt=login&acr_values=urn:giyus:acr:mfa&max_age=0`) e repete a chamada — no lugar (modo fake) ou após
// o retorno do redirect (ação pendente em sessionStorage, `stepUpPending.ts`).
import { GiyusApiError } from '@giyus/api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { create } from 'zustand';

import { useAuth } from './authContext';
import {
  type PendingStepUp,
  browserPendingStorage,
  clearPendingStepUp,
  readPendingStepUp,
  writePendingStepUp,
} from './stepUpPending';

export const STEP_UP_ERROR_CODE = 'PLT_STEP_UP_REQUIRED';

/** `401` com `error=step_up_required` (OAuth) ou `errorCode=PLT_STEP_UP_REQUIRED` (ProblemDetails). */
export function isStepUpRequired(error: unknown): boolean {
  if (!(error instanceof GiyusApiError) || error.status !== 401) {
    return false;
  }
  return error.errorCode === STEP_UP_ERROR_CODE || error.problem['error'] === 'step_up_required';
}

interface StepUpState {
  readonly open: boolean;
  readonly returnTo: string;
  readonly pending: PendingStepUp | null;
  /** Repetição imediata quando o step-up é satisfeito sem redirect (modo fake). */
  readonly resumeInPlace: (() => Promise<void>) | null;
  readonly request: (
    returnTo: string,
    pending?: PendingStepUp | null,
    resumeInPlace?: (() => Promise<void>) | null,
  ) => void;
  readonly close: () => void;
}

export const useStepUpStore = create<StepUpState>((set) => ({
  open: false,
  returnTo: '/',
  pending: null,
  resumeInPlace: null,
  request: (returnTo, pending = null, resumeInPlace = null) => {
    set({ open: true, returnTo, pending, resumeInPlace });
  },
  close: () => {
    set({ open: false, pending: null, resumeInPlace: null });
  },
}));

function currentPath(): string {
  return typeof window === 'undefined'
    ? '/'
    : `${window.location.pathname}${window.location.search}`;
}

export interface StepUpApi {
  readonly open: boolean;
  /** Abre o diálogo; a ação pendente (chave + carga) é retomada após o retorno. */
  readonly requestStepUp: (
    returnTo?: string,
    pending?: PendingStepUp | null,
    resumeInPlace?: (() => Promise<void>) | null,
  ) => void;
  /** Confirma no diálogo: redireciona ao provedor (persistindo a ação pendente) ou repete a ação no lugar. */
  readonly confirm: () => Promise<void>;
  readonly cancel: () => void;
  readonly isStepUpRequired: typeof isStepUpRequired;
}

export function useStepUp(): StepUpApi {
  const { backend } = useAuth();
  const open = useStepUpStore((s) => s.open);
  const request = useStepUpStore((s) => s.request);
  const close = useStepUpStore((s) => s.close);

  const requestStepUp = useCallback<StepUpApi['requestStepUp']>(
    (target, pending, resumeInPlace) => {
      request(target ?? currentPath(), pending ?? null, resumeInPlace ?? null);
    },
    [request],
  );

  const confirm = useCallback(async () => {
    const { returnTo, pending, resumeInPlace } = useStepUpStore.getState();
    // Persistir ANTES de redirecionar: `signinRedirect` do oidc-client-ts navega e nunca resolve antes de a página
    // descarregar — o que vier depois do `await` não executa. No modo fake (sem redirect) a pendência é apagada.
    if (pending) {
      writePendingStepUp(browserPendingStorage(), { ...pending, returnTo });
    }
    const redirected = await backend.stepUp(returnTo);
    if (redirected) {
      // A página descarrega no redirect; o diálogo some com ela.
      return;
    }
    clearPendingStepUp(browserPendingStorage());
    close();
    if (resumeInPlace) {
      try {
        await resumeInPlace();
      } catch {
        // A ação repetida falhou por outro motivo: quem a disparou (mutation) já expõe o erro na tela.
      }
    }
  }, [backend, close]);

  return { open, requestStepUp, confirm, cancel: close, isStepUpRequired };
}

export interface StepUpActionOptions<TInput> {
  /** Chave estável da ação (uma por tela/ação). */
  readonly key: string;
  /** A chamada protegida (lança `GiyusApiError` de step-up quando o token não tem `auth_time` recente). */
  readonly run: (input: TInput) => Promise<void>;
  /** Carga serializável guardada para repetir a ação após o redirect; omitir quando a entrada tem segredos (senhas). */
  readonly toPayload?: (input: TInput) => unknown;
  /** Reconstrói a entrada a partir da carga guardada; `null` quando não dá para repetir automaticamente. */
  readonly fromPayload?: (payload: unknown) => TInput | null;
}

export interface StepUpAction<TInput> {
  /** Executa; devolve `true` quando concluiu e `false` quando ficou aguardando o step-up. Outros erros propagam. */
  readonly execute: (input: TInput) => Promise<boolean>;
  /**
   * `true` quando a página remontou após um step-up desta ação sem carga para repetir (ou a repetição falhou):
   * a tela pede ao usuário que refaça a ação — já reautenticado.
   */
  readonly resumedWithoutPayload: boolean;
}

/** Envolve uma ação protegida por step-up: intercepta o `401`, pede a reautenticação e repete (no lugar ou após o retorno). */
export function useStepUpAction<TInput>(
  options: StepUpActionOptions<TInput>,
): StepUpAction<TInput> {
  const { key, run, toPayload, fromPayload } = options;
  const { requestStepUp } = useStepUp();
  // Leitura pura na montagem (o consumo — apagar — acontece no efeito abaixo, uma única vez).
  const [stored] = useState(() => {
    const pending = readPendingStepUp(browserPendingStorage());
    return pending?.key === key ? pending : null;
  });
  const [resumeFailed, setResumeFailed] = useState(false);
  const runRef = useRef(run);
  useEffect(() => {
    runRef.current = run;
  });

  const execute = useCallback(
    async (input: TInput): Promise<boolean> => {
      try {
        await runRef.current(input);
        return true;
      } catch (error: unknown) {
        if (!isStepUpRequired(error)) {
          throw error;
        }
        const pending: PendingStepUp = { key, payload: toPayload ? toPayload(input) : null };
        requestStepUp(currentPath(), pending, () => runRef.current(input));
        return false;
      }
    },
    [key, toPayload, requestStepUp],
  );

  const resumeInput =
    stored && stored.payload !== null && fromPayload ? fromPayload(stored.payload) : null;

  useEffect(() => {
    if (!stored) {
      return;
    }
    clearPendingStepUp(browserPendingStorage());
    if (resumeInput === null) {
      return;
    }
    runRef.current(resumeInput).catch(() => {
      // A repetição falhou (ex.: o step-up não bastou): a tela pede que o usuário refaça a ação.
      setResumeFailed(true);
    });
    // Só na montagem: `stored`/`resumeInput` não mudam depois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    execute,
    resumedWithoutPayload: (stored !== null && resumeInput === null) || resumeFailed,
  };
}
